import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Control, useFieldArray, UseFormSetValue, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import { Input } from '../../../components/input/index';
import {
  DividendPayoutType,
  DividendShareClassHolder,
} from '../../../hooks/dividend/dividend.types';
import { DividendHooks } from '../../../hooks/index';
import { useShareClasses } from '../../../hooks/share-class/use-share-classes';
import { useInfiniteStakeholders } from '../../../hooks/stakeholder/use-infinite-stakeholders';
import { StakeholderOrder } from '../../../types/stakeholder.types';
import { getTranslation } from '../../../utils/get-translation.util';
import { formatDividendAmount } from '../utils/format-dividend-amount.util';
import {
  AddDividendFormData,
  DIVIDEND_PER_SHARE_MAX_DECIMALS,
  FormItem,
} from './add-dividend.validation';
import { DividendSelectField } from './dividend-select-field';
import { DividendStakeholderCombobox } from './dividend-stakeholder-combobox';

type Props = {
  companyId: string;
  control: Control<AddDividendFormData>;
  currencySymbol: string;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
  setValue: UseFormSetValue<AddDividendFormData>;
};

const EMPTY_ITEM: FormItem = {
  amountPerShare: undefined,
  shareClassId: undefined,
  shareClassName: undefined,
  stakeholderId: undefined,
  stakeholderName: undefined,
  totalAmount: undefined,
};

const SummaryRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <>
    <div className="h-px w-full bg-gray-100" />
    <div className="flex items-center justify-between px-4">
      <span className="text-sm font-[450] text-gray-700">{label}</span>
      <span className="rounded bg-brand-25 px-3 py-1 text-sm font-[550] text-brand-700">
        {value}
      </span>
    </div>
  </>
);

export const AddDividendStepTwo: FC<Props> = ({
  companyId,
  control,
  currencySymbol,
  onBack,
  onCancel,
  onNext,
  setValue,
}) => {
  const navigate = useNavigate();
  const type = useWatch({ control, name: 'type' });
  const isShareClass = type === DividendPayoutType.PER_SHARE_CLASS;

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const rawItems = useWatch({ control, name: 'items' });
  const watchedItems = useMemo(() => rawItems ?? [], [rawItems]);

  // The step is remounted on every entry, so the first payout card is expanded by default —
  // including when the user goes back, changes the period and returns (86cayfurk).
  const [expandedIndex, setExpandedIndex] = useState<null | number>(0);

  // Always show one empty item by default when entering the step with no items yet,
  // expanded so the user lands straight on an editable card.
  useEffect(() => {
    if (fields.length === 0) {
      append({ ...EMPTY_ITEM });
      setExpandedIndex(0);
    }
  }, [append, fields.length]);

  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });
  const { stakeholders: allStakeholders } = useInfiniteStakeholders({
    companyId,
    search: '',
    sortOrder: StakeholderOrder.ASC,
  });
  const { holdersByShareClass } = DividendHooks.useDividendShareClassHolders({ companyId });

  const usedClassIds = useMemo(
    () => new Set(watchedItems.map((i) => i.shareClassId).filter(Boolean)),
    [watchedItems],
  );
  const usedComboKeys = useMemo(
    () => new Set(watchedItems.map((i) => `${i.shareClassId}:${i.stakeholderId}`)),
    [watchedItems],
  );

  // Only classes that both grant dividend rights AND have shares held by stakeholders — a
  // per-share-class payout on a class with no holders would distribute nothing.
  const dividendRightClasses = useMemo(
    () => shareClasses.filter((sc) => sc.dividendRightEnabled && Number(sc.totalShares) > 0),
    [shareClasses],
  );

  // Keep Total Payout in sync: Per Share Class = Total Shares × rate; Per Stakeholder =
  // the stakeholder's shares in the selected class × rate.
  useEffect(() => {
    watchedItems.forEach((item, index) => {
      let nextTotal: number | undefined;
      if (isShareClass) {
        const shareClass = shareClasses.find((sc) => sc.id === item.shareClassId);
        nextTotal =
          shareClass?.totalShares && item.amountPerShare
            ? shareClass.totalShares * item.amountPerShare
            : undefined;
      } else {
        const shares = (holdersByShareClass.get(item.shareClassId ?? '') ?? []).find(
          (holder) => holder.stakeholderId === item.stakeholderId,
        )?.shares;
        nextTotal = shares && item.amountPerShare ? shares * item.amountPerShare : undefined;
      }
      if (item.totalAmount !== nextTotal) {
        setValue(`items.${index}.totalAmount`, nextTotal);
      }
    });
  }, [isShareClass, watchedItems, shareClasses, holdersByShareClass, setValue]);

  const handleAddItem = () => {
    const newIndex = fields.length;
    append({ ...EMPTY_ITEM });
    setExpandedIndex(newIndex);
  };

  const handleRemove = (index: number) => {
    remove(index);
    setExpandedIndex(fields.length > 1 ? Math.max(0, index - 1) : null);
  };

  const areAllItemsValid =
    watchedItems.length > 0 &&
    watchedItems.every((item) =>
      isShareClass
        ? item.shareClassId && item.amountPerShare && item.amountPerShare > 0
        : item.shareClassId && item.stakeholderId && item.amountPerShare && item.amountPerShare > 0,
    );

  // Either field may be filled first, so each list is narrowed by the other: with no class picked
  // the stakeholder list spans every dividend-right class, and picking a stakeholder narrows the
  // classes to the ones they actually hold.
  const holdersAcrossClasses = useMemo(() => {
    const byStakeholderId = new Map<string, DividendShareClassHolder>();

    dividendRightClasses.forEach((shareClass) => {
      (holdersByShareClass.get(shareClass.id) ?? []).forEach((holder) => {
        if (!byStakeholderId.has(holder.stakeholderId))
          byStakeholderId.set(holder.stakeholderId, holder);
      });
    });

    return [...byStakeholderId.values()];
  }, [dividendRightClasses, holdersByShareClass]);

  const classIdsByStakeholder = useMemo(() => {
    const map = new Map<string, Set<string>>();

    dividendRightClasses.forEach((shareClass) => {
      (holdersByShareClass.get(shareClass.id) ?? []).forEach(({ stakeholderId }) => {
        const owned = map.get(stakeholderId);
        if (owned) owned.add(shareClass.id);
        else map.set(stakeholderId, new Set([shareClass.id]));
      });
    });

    return map;
  }, [dividendRightClasses, holdersByShareClass]);

  const hasNoEligibleShareClasses = dividendRightClasses.length === 0;

  const addShareClassAction = (
    <Button
      className="w-full justify-start text-nowrap rounded bg-brand-25 px-2 py-[6px] text-start text-sm font-[450] text-brand-700"
      onClick={() => navigate('/share-classes?openAddModal=true')}
      styleType="NONE"
      type="button"
    >
      + {getTranslation('ADD_SHARE_CLASS')}
    </Button>
  );

  const formatNumber = (n: number) => formatDividendAmount(n);

  return (
    <>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white">
        <div className="flex flex-col gap-6 px-6 pb-6 pt-4">
          <>
            {fields.map((field, index) => {
              const item = watchedItems[index] ?? {};
              const isExpanded = expandedIndex === index;

              const shareClass = shareClasses.find((sc) => sc.id === item.shareClassId);
              const totalPayout =
                shareClass?.totalShares && item.amountPerShare
                  ? shareClass.totalShares * item.amountPerShare
                  : null;

              const resolvedShareClassName =
                item.shareClassName ?? shareClasses.find((sc) => sc.id === item.shareClassId)?.name;
              const resolvedStakeholderName =
                item.stakeholderName ??
                allStakeholders.find((s) => s.id === item.stakeholderId)?.fullName;

              const headerLabel = isShareClass
                ? (resolvedShareClassName ?? getTranslation('NEW_SHARE_CLASS_PAYOUT'))
                : (resolvedStakeholderName ?? getTranslation('NEW_STAKEHOLDER_PAYOUT'));

              const stakeholderClassIds = item.stakeholderId
                ? classIdsByStakeholder.get(item.stakeholderId)
                : undefined;

              const availableShareClasses = dividendRightClasses.filter((sc) =>
                isShareClass
                  ? !usedClassIds.has(sc.id) || sc.id === item.shareClassId
                  : !stakeholderClassIds || stakeholderClassIds.has(sc.id),
              );

              const classHolders = holdersByShareClass.get(item.shareClassId ?? '') ?? [];
              const selectableHolders = item.shareClassId ? classHolders : holdersAcrossClasses;
              const availableHolders = selectableHolders.filter(
                (holder) =>
                  holder.stakeholderId === item.stakeholderId ||
                  !usedComboKeys.has(`${item.shareClassId}:${holder.stakeholderId}`),
              );
              const stakeholderShares =
                classHolders.find((holder) => holder.stakeholderId === item.stakeholderId)
                  ?.shares ?? 0;
              const stakeholderTotal =
                stakeholderShares && item.amountPerShare
                  ? stakeholderShares * item.amountPerShare
                  : null;

              return (
                <div
                  className={twMerge(
                    'rounded-md',
                    isExpanded
                      ? 'flex flex-col gap-3 bg-gray-50 px-0.5 pb-0.5 pt-3'
                      : 'border border-gray-100 bg-white',
                  )}
                  key={field.id}
                >
                  <div
                    className={twMerge(
                      'flex cursor-pointer items-center justify-between',
                      isExpanded ? 'px-3.5' : 'h-12 px-4',
                    )}
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                  >
                    <span className="text-sm font-[450] text-gray-700">{headerLabel}</span>
                    <div className="flex items-center gap-4">
                      {fields.length > 1 && (
                        <button
                          className="text-xs font-[550] text-fireside-600 hover:underline"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemove(index);
                          }}
                          type="button"
                        >
                          {getTranslation('REMOVE')}
                        </button>
                      )}
                      <ChevronDownIcon
                        className={twMerge(
                          'h-4 w-4 shrink-0 transition-transform duration-200',
                          isExpanded && 'rotate-180',
                        )}
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="rounded-md bg-white py-3 shadow-[0px_-1px_3px_0px_rgba(16,24,40,0.04),0px_2px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)]">
                      <div className="flex flex-col gap-3">
                        {isShareClass ? (
                          <>
                            <div className="flex items-center gap-4 px-4">
                              <span className="flex-1 text-sm font-[450] text-gray-700">
                                {getTranslation('SHARE_CLASS')}
                              </span>
                              <div className="w-56">
                                <DividendSelectField
                                  control={control}
                                  emptyMessage={getTranslation(
                                    'NO_SHARE_CLASSES_WITH_DIVIDEND_RIGHTS',
                                  )}
                                  footerAction={addShareClassAction}
                                  name={`items.${index}.shareClassId`}
                                  onSelect={(value) =>
                                    setValue(
                                      `items.${index}.shareClassName`,
                                      shareClasses.find((s) => s.id === value)?.name,
                                    )
                                  }
                                  options={availableShareClasses.map((sc) => ({
                                    label: sc.name,
                                    value: sc.id,
                                  }))}
                                  placeholder={getTranslation('CLASS')}
                                />
                              </div>
                            </div>

                            <div className="flex items-center gap-4 px-4">
                              <span className="flex-1 text-sm font-[450] text-gray-700">
                                {getTranslation('DIVIDEND_PER_SHARE')}
                              </span>
                              <div className="w-56">
                                <Input
                                  maxDecimalPlaces={DIVIDEND_PER_SHARE_MAX_DECIMALS}
                                  numberOnly
                                  onChange={(val) => {
                                    const parsed = parseFloat(val);
                                    setValue(
                                      `items.${index}.amountPerShare`,
                                      isNaN(parsed) ? undefined : parsed,
                                    );
                                    const sc = shareClasses.find((s) => s.id === item.shareClassId);
                                    if (sc?.totalShares && !isNaN(parsed)) {
                                      setValue(
                                        `items.${index}.totalAmount`,
                                        sc.totalShares * parsed,
                                      );
                                    } else {
                                      setValue(`items.${index}.totalAmount`, undefined);
                                    }
                                  }}
                                  placeholder={getTranslation('VALUE')}
                                  shouldFormatNumber
                                  value={
                                    item.amountPerShare !== undefined
                                      ? String(item.amountPerShare)
                                      : ''
                                  }
                                />
                              </div>
                            </div>

                            {shareClass && (
                              <div className="flex flex-col gap-3">
                                <SummaryRow
                                  label={getTranslation('TOTAL_SHARES')}
                                  value={formatNumber(shareClass.totalShares ?? 0)}
                                />
                                <SummaryRow
                                  label={getTranslation('TOTAL_PAYOUT')}
                                  value={
                                    totalPayout !== null
                                      ? `${currencySymbol} ${formatNumber(totalPayout)}`
                                      : '—'
                                  }
                                />
                                <SummaryRow
                                  label={getTranslation('IMPACTED_STAKEHOLDERS')}
                                  value={shareClass.stakeholders ?? 0}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-4 px-4">
                              <span className="flex-1 text-sm font-[450] text-gray-700">
                                {getTranslation('SHARE_CLASS')}
                              </span>
                              <div className="w-56">
                                <DividendSelectField
                                  control={control}
                                  emptyMessage={getTranslation(
                                    'NO_SHARE_CLASSES_WITH_DIVIDEND_RIGHTS',
                                  )}
                                  footerAction={addShareClassAction}
                                  name={`items.${index}.shareClassId`}
                                  onSelect={(value) => {
                                    setValue(
                                      `items.${index}.shareClassName`,
                                      shareClasses.find((s) => s.id === value)?.name,
                                    );
                                    const keepsStakeholder =
                                      !!item.stakeholderId &&
                                      !!classIdsByStakeholder
                                        .get(item.stakeholderId)
                                        ?.has(String(value));

                                    if (!keepsStakeholder) {
                                      setValue(`items.${index}.stakeholderId`, undefined);
                                      setValue(`items.${index}.stakeholderName`, undefined);
                                    }
                                  }}
                                  options={availableShareClasses.map((sc) => ({
                                    label: sc.name,
                                    value: sc.id,
                                  }))}
                                  placeholder={getTranslation('CLASS')}
                                />
                              </div>
                            </div>

                            <div className="flex items-center gap-4 px-4">
                              <span className="flex-1 text-sm font-[450] text-gray-700">
                                {getTranslation('STAKEHOLDER')}
                              </span>
                              <div className="w-56">
                                <DividendStakeholderCombobox
                                  control={control}
                                  name={`items.${index}.stakeholderId`}
                                  onSelect={(value) =>
                                    setValue(
                                      `items.${index}.stakeholderName`,
                                      selectableHolders.find((h) => h.stakeholderId === value)
                                        ?.fullName,
                                    )
                                  }
                                  options={availableHolders.map((holder) => ({
                                    label: holder.fullName,
                                    value: holder.stakeholderId,
                                  }))}
                                  placeholder={getTranslation('STAKEHOLDER')}
                                />
                                {item.stakeholderId && (
                                  <span className="mt-2 block pl-2.5 text-xs font-[450] text-gray-600">
                                    {getTranslation('SHARES')}: {formatNumber(stakeholderShares)}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-4 px-4">
                              <span className="flex-1 text-sm font-[450] text-gray-700">
                                {getTranslation('DIVIDEND_PER_SHARE')}
                              </span>
                              <div className="w-56">
                                <Input
                                  maxDecimalPlaces={DIVIDEND_PER_SHARE_MAX_DECIMALS}
                                  numberOnly
                                  onChange={(val) => {
                                    const parsed = parseFloat(val);
                                    setValue(
                                      `items.${index}.amountPerShare`,
                                      isNaN(parsed) ? undefined : parsed,
                                    );
                                  }}
                                  placeholder={getTranslation('VALUE')}
                                  shouldFormatNumber
                                  value={
                                    item.amountPerShare !== undefined
                                      ? String(item.amountPerShare)
                                      : ''
                                  }
                                />
                              </div>
                            </div>

                            {item.stakeholderId && (
                              <div className="flex flex-col gap-3">
                                <SummaryRow
                                  label={getTranslation('TOTAL_PAYOUT')}
                                  value={
                                    stakeholderTotal !== null
                                      ? `${currencySymbol} ${formatNumber(stakeholderTotal)}`
                                      : '—'
                                  }
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <button className="self-start py-2 pl-4" onClick={handleAddItem} type="button">
              <span className="border-b border-brand-700 text-sm font-medium text-brand-700">
                +{' '}
                {getTranslation(
                  isShareClass ? 'ADD_NEW_SHARE_CLASS_PAYOUT' : 'ADD_NEW_STAKEHOLDER_PAYOUT',
                )}
              </span>
            </button>
          </>
        </div>
      </div>

      <div className="flex w-full shrink-0 items-center justify-between px-6">
        <Button
          className="h-9 w-fit bg-transparent px-3 py-1.5 text-sm font-[450] text-gray-700"
          onClick={onCancel}
          styleType="NONE"
          type="button"
        >
          {getTranslation('CANCEL')}
        </Button>
        <div className="flex items-center gap-3">
          <Button
            className="h-9 w-fit rounded border border-gray-300 bg-white px-4 py-2.5 text-sm font-[450] text-gray-700"
            onClick={onBack}
            styleType="NONE"
            type="button"
          >
            {getTranslation('BACK')}
          </Button>
          <Button
            className="h-9 w-fit px-4 py-2.5 text-sm font-[550] text-white disabled:opacity-50"
            disabled={!areAllItemsValid || hasNoEligibleShareClasses}
            onClick={onNext}
            type="button"
          >
            {getTranslation('NEXT')}
          </Button>
        </div>
      </div>
    </>
  );
};
